/**
 * OnboardingView - First-time language pair selection
 *
 * Two conversational steps:
 *   Step 1: "I speak..." — pick native language
 *   Step 2: "I'm learning..." — pick target language
 */

import { useState, useMemo } from 'react'
import { getPairDefinitions } from '@shared/languages'
import type { LanguagePairDefinition, LanguageDefinition } from '@shared/languages/types'
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton'
import { cn } from '@/lib/utils'
import { useT } from '@/contexts/I18nContext'
import { track } from '@/lib/betterstack'

interface OnboardingViewProps {
  onComplete: () => void
}

// Build flat list of all unique languages from pair definitions
function getAllLanguages(pairs: LanguagePairDefinition[]): LanguageDefinition[] {
  const seen = new Set<string>()
  const result: LanguageDefinition[] = []
  for (const pair of pairs) {
    for (const lang of Object.values(pair.languages)) {
      if (!seen.has(lang.lang)) {
        seen.add(lang.lang)
        result.push(lang)
      }
    }
  }
  return result
}

// Find available target languages given a native language
function getTargetsForNative(pairs: LanguagePairDefinition[], nativeLang: string): LanguageDefinition[] {
  const targets: LanguageDefinition[] = []
  for (const pair of pairs) {
    const langs = Object.values(pair.languages)
    const hasNative = langs.some(l => l.lang === nativeLang)
    if (!hasNative) continue
    const target = langs.find(l => l.lang !== nativeLang)
    if (target) targets.push(target)
  }
  return targets
}

// Find the profile ID for a given native + target pair
function findProfileId(native: string, target: string): string {
  // Profile ID format: ${targetLang}-${nativeLang}
  return `${target}-${native}`
}

export function OnboardingView({ onComplete }: OnboardingViewProps) {
  const t = useT()
  const [nativeLang, setNativeLang] = useState<string | null>(null)
  const [targetLang, setTargetLang] = useState<string | null>(null)
  const [learningContext, setLearningContext] = useState('')

  const pairs = getPairDefinitions()
  const allLanguages = useMemo(() => getAllLanguages(pairs), [pairs])

  const targets = nativeLang ? getTargetsForNative(pairs, nativeLang) : []

  const handleNativeSelect = (lang: string) => {
    setNativeLang(lang)
    setTargetLang(null)
  }

  const handleStart = () => {
    if (!nativeLang || !targetLang) return
    const profileId = findProfileId(nativeLang, targetLang)
    // Persist directly to localStorage — do NOT use React state setters.
    // This component unmounts when onComplete() triggers parent re-render,
    // so React may discard pending updater functions without running them.
    // A direct write guarantees the data survives the unmount.
    try {
      const raw = localStorage.getItem('settings') || '{}'
      const current = JSON.parse(raw)
      localStorage.setItem('settings', JSON.stringify({
        ...current,
        languagePair: profileId,
        onboarded: true,
        learningContext: learningContext.trim(),
      }))
    } catch { /* storage unavailable */ }
    track('onboarding-complete', { profileId })
    onComplete()
  }

  // Step 1: pick native language
  if (!nativeLang) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[100dvh] bg-surface px-6">
        <div className="w-full max-w-sm">
          <h1 className="font-headline text-2xl font-bold text-on-surface text-center mb-8">
            {t('onboarding.iSpeak')}
          </h1>
          <div className="grid grid-cols-2 gap-3">
            {allLanguages.map((lang) => (
              <button
                key={lang.lang}
                onClick={() => handleNativeSelect(lang.lang)}
                className={cn(
                  'flex flex-col items-center gap-1.5 rounded-2xl p-4 border cursor-pointer',
                  'bg-surface-container-lowest border-hairline shadow-subtle',
                  'transition-all duration-200 active:scale-[0.98]',
                  'hover:border-primary/40 hover:shadow-card',
                )}
              >
                <span className="font-headline text-2xl text-on-surface">{lang.scriptSample}</span>
                <span className="text-xs text-on-surface-variant uppercase tracking-wide">{lang.langName}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    )
  }

  // Step 2: pick target language
  if (!targetLang) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[100dvh] bg-surface px-6">
        <div className="w-full max-w-sm">
          <h1 className="font-headline text-2xl font-bold text-on-surface text-center mb-8">
            {t('onboarding.imLearning')}
          </h1>
          <div className="grid grid-cols-2 gap-3">
            {targets.map((lang) => (
              <button
                key={lang.lang}
                onClick={() => setTargetLang(lang.lang)}
                className={cn(
                  'flex flex-col items-center gap-1.5 rounded-2xl p-4 border cursor-pointer',
                  'bg-surface-container-lowest border-hairline shadow-subtle',
                  'transition-all duration-200 active:scale-[0.98]',
                  'hover:border-primary/40 hover:shadow-card',
                )}
              >
                <span className="font-headline text-2xl text-on-surface">{lang.scriptSample}</span>
                <span className="text-xs text-on-surface-variant uppercase tracking-wide">{lang.langName}</span>
              </button>
            ))}
          </div>
          <button
            onClick={() => { setNativeLang(null); setTargetLang(null) }}
            className="block mx-auto mt-6 text-sm text-muted hover:text-on-surface transition-colors"
          >
            {t('onboarding.back')}
          </button>
        </div>
      </div>
    )
  }

  // Confirmation
  const nativeDef = allLanguages.find(l => l.lang === nativeLang)!
  const targetDef = targets.find(l => l.lang === targetLang)!

  return (
    <div className="flex flex-col items-center justify-center min-h-[100dvh] bg-surface px-6">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="flex flex-col items-center">
              <span className="font-headline text-3xl text-on-surface">{nativeDef.scriptSample}</span>
              <span className="text-xs text-on-surface-variant uppercase tracking-wide mt-1">{nativeDef.langName}</span>
            </div>
            <span className="text-xl text-primary font-bold">→</span>
            <div className="flex flex-col items-center">
              <span className="font-headline text-3xl text-on-surface">{targetDef.scriptSample}</span>
              <span className="text-xs text-on-surface-variant uppercase tracking-wide mt-1">{targetDef.langName}</span>
            </div>
          </div>
        </div>

        {/* Optional learning goal — tunes AI generation. Skippable. */}
        <div className="mb-6">
          <label htmlFor="onboarding-context" className="block text-center text-sm text-on-surface-variant mb-2">
            {t('onboarding.learningContext.label')}
          </label>
          <textarea
            id="onboarding-context"
            value={learningContext}
            onChange={(e) => setLearningContext(e.target.value)}
            placeholder={t('onboarding.learningContext.placeholder')}
            rows={2}
            maxLength={500}
            className="w-full px-4 py-3 bg-surface-container-lowest border border-hairline rounded-xl text-on-surface text-sm font-medium resize-none focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition-all"
          />
        </div>

        <PrimaryButton size="lg" fullWidth onClick={handleStart}>
          {t('onboarding.startLearning')}
        </PrimaryButton>

        <p className="text-center text-muted-soft text-xs mt-3">
          {t('onboarding.changeAnytime')}
        </p>

        <button
          onClick={() => setTargetLang(null)}
          className="block mx-auto mt-4 text-sm text-muted hover:text-on-surface transition-colors"
        >
          {t('onboarding.back')}
        </button>
      </div>
    </div>
  )
}
